import { MapPin, Ticket, UserCheck, Wrench, LucideIcon } from 'lucide-react';
import { ActivityType } from 'app/data/types/activity';

/**
 * Per-type presentation config — the icon, the filter-chip label, and the source module name
 * (used for the empty state and deeplink telemetry). Type hues from the mocks are semantic
 * recognition cues, not the tenant accent.
 */
interface ActivityTypeConfig {
  icon: LucideIcon;
  /** react-intl id for the plural filter label. */
  filterLabelId: string;
  /** react-intl id for the empty-state title. */
  emptyTitleId: string;
  /** react-intl id for the empty-state body. */
  emptyBodyId: string;
  /** react-intl id for the empty-state CTA. Doubles as the end-of-list CTA's button label — the
   *  action is the same one, so the two never disagree about what it is called. */
  emptyCtaId: string;
  /** react-intl id for the line above the end-of-list CTA. */
  endCtaPromptId: string;
  /** Human module name (telemetry / fallbacks). */
  module: string;
}

export const ACTIVITY_TYPE_CONFIG: Record<ActivityType, ActivityTypeConfig> = {
  work_order: {
    // The same fallbacks the tenant portal uses for these verticals, so a building that configured no
    // icon of its own reads identically in the feed and in the portal it opened out of. A building that
    // DID configure one overrides this via its utility button.
    icon: Wrench,
    filterLabelId: 'activity.filter.requests',
    emptyTitleId: 'activity.empty.requests.title',
    emptyBodyId: 'activity.empty.requests.body',
    emptyCtaId: 'activity.empty.requests.cta',
    endCtaPromptId: 'activity.endCta.requests.prompt',
    module: 'Service Requests',
  },
  resource_booking: {
    icon: MapPin,
    filterLabelId: 'activity.filter.bookings',
    emptyTitleId: 'activity.empty.bookings.title',
    emptyBodyId: 'activity.empty.bookings.body',
    emptyCtaId: 'activity.empty.bookings.cta',
    endCtaPromptId: 'activity.endCta.bookings.prompt',
    module: 'Resource Booking',
  },
  event: {
    icon: Ticket,
    filterLabelId: 'activity.filter.events',
    emptyTitleId: 'activity.empty.events.title',
    emptyBodyId: 'activity.empty.events.body',
    emptyCtaId: 'activity.empty.events.cta',
    endCtaPromptId: 'activity.endCta.events.prompt',
    module: 'Events',
  },
  visitor_registration: {
    icon: UserCheck,
    filterLabelId: 'activity.filter.visitors',
    emptyTitleId: 'activity.empty.visitors.title',
    emptyBodyId: 'activity.empty.visitors.body',
    emptyCtaId: 'activity.empty.visitors.cta',
    endCtaPromptId: 'activity.endCta.visitors.prompt',
    module: 'Visitor Registration',
  },
};

/**
 * Abstract action type → button label id. The action `type` is the FE's single source of truth for
 * an action; an unknown type has no mapping here and the card omits it (actions degrade, never
 * dead-tap).
 */
export const ACTION_LABEL_ID: Record<string, string> = {
  open_record: 'activity.action.view',
  approve_estimate: 'activity.action.approveEstimate',
  book_again: 'activity.action.bookAgain',
};

/**
 * The actions that do NOT open the record they sit on.
 *
 * `book_again` is a creation flow — it opens the resource picker to make a new booking — so it
 * leaves the feed rather than filling the pane. Everything else (`open_record`, `approve_estimate`)
 * opens the record itself.
 *
 * Shared because two places answer to it and must not drift: the feed decides whether to move the
 * highlight, and the deeplink decides whether to remember the record for the host's pane. A row
 * lit up beside a pane showing a creation flow is exactly the disagreement this prevents.
 */
export const NON_RECORD_ACTIONS: ReadonlySet<string> = new Set(['book_again']);

/** Chip filter value — either a type or the catch-all. */
export type ActivityFilter = ActivityType | 'all';

/** Filter chip order, left to right. Mirrors the mock. */
export const ACTIVITY_FILTER_ORDER: ActivityFilter[] = [
  'all',
  'work_order',
  'resource_booking',
  'event',
  'visitor_registration',
];

/** A URL param value → a valid filter; anything unrecognized falls back to the `all` catch-all. */
export const resolveActivityFilter = (value: string | null): ActivityFilter =>
  (value && ACTIVITY_FILTER_ORDER.includes(value as ActivityFilter) ? (value as ActivityFilter) : 'all');
